import {
  FullAsinWithAudits,
  FullAsin,
  RestrictedAsin,
  AsinAudit,
} from "../../from-server/asin-central/model";
import { serviceToDisplay } from "../../util/dateUtils";
import {
  FlashbarProps,
  Popover,
  Icon,
  Link,
} from "@amzn/awsui-components-react/polaris";
import React from "react";
import { LAUNCH_ASIN_TYPES } from "../../constants/launchAsinTypes";
import { PPLS_REQUEST_ACCESS } from "../../constants/externalLink";
import { exportCSV, getTimestampedCsvFileName } from "../../util/fileUtils";
import {
  getIconForValidationAttribute,
  VALIDATION_ATTRIBUTE_NAME_MAP,
  VALIDATION_ATTRIBUTE_OVERALL_RESULT,
  ValidationAttribute,
  ValidationAttributeOverallStatus,
} from "../validation/validationAttribute";
import { useDispatch } from "react-redux";
import { setValidationModalState } from "../validation/validationAttributeModalConnector";
import { getQueryFromUrl } from "../../util/urlUtils";
import AsinDisplayCell from "../common/AsinDisplayCell";
import { convertMapWithMainKeyToString } from "../../util/mapUtils";
import { AsinMarketplaceDate } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice/dist/umd/AsinMarketplaceDate";
import { LVS_ATTRIBUTES_IN_SCOPE } from "../validation/validationAttributeModal";

export const formattedRestrictedPorAsinProgramName = (entry: RestrictedAsin) =>
  `Restricted Access (${
    entry.restrictedProgramName || entry.restrictedProgramId
  })`;

export const formattedBundleDescription = (val: string) => {
  // Transforms descriptions that contain Markdown italic markups to italics.
  // This is used to identify the bundle description components (item_name) that are
  // sourced from Catalog, e.g., (Suez, Black, 16GB, Device) and (*Suez, Black, 32GB, Device*).
  const emRegex = /\*(.*?)\*/g;
  const elements = val.split(emRegex).map((part, index) => {
    if (index % 2 === 1) {
      return <em key={index}>{part}</em>;
    } else {
      return <span key={index}>{part}</span>;
    }
  });
  return <>{elements}</>;
};

function cellWithAudit(val: string, audit: AsinAudit) {
  const entity =
    audit.lastUpdatedBy!!.identity === "DeviceAsinListener"
      ? "Retail Catalog"
      : audit.lastUpdatedBy!!.identity;
  return (
    <>
      {val}
      <Popover
        dismissButton={false}
        position="top"
        size="medium"
        triggerType="text"
        content={`Last edit by ${entity} on ${serviceToDisplay(
          audit.lastUpdatedAt!!
        )}`}
      >
        {" "}
        ...
      </Popover>
    </>
  );
}

function validationAttributeCellWithAudit(
  item: FullAsinWithAudits,
  attributeName: string,
  overallStatus: ValidationAttributeOverallStatus,
  modalValidationAttribute: ValidationAttribute,
  audit: AsinAudit
) {
  const query = getQueryFromUrl();
  const dispatch = useDispatch();
  const statusIcon = (
    <>
      {getIconForValidationAttribute(overallStatus)}
      &nbsp;
      {attributeName.toLowerCase().includes("manual") ? (
        VALIDATION_ATTRIBUTE_OVERALL_RESULT[overallStatus]
      ) : (
        <a
          // Keep the URL parameters in the URL.
          href={"#" + query}
          onClick={() =>
            dispatch(
              setValidationModalState({
                isOpen: true,
                detailSteps: modalValidationAttribute.detail,
                initialDetailSteps: modalValidationAttribute.detail,
                initialDetailStepsFailedLVS: modalValidationAttribute.detail
                  ? Array.from(modalValidationAttribute.detail).filter(
                      (step) =>
                        step.result == "fail" &&
                        LVS_ATTRIBUTES_IN_SCOPE.has(step.step)
                    )
                  : [],
                detailStepsFailedLVS: modalValidationAttribute.detail
                  ? Array.from(modalValidationAttribute.detail).filter(
                      (step) =>
                        step.result == "fail" &&
                        LVS_ATTRIBUTES_IN_SCOPE.has(step.step)
                    )
                  : [],
                title: `${VALIDATION_ATTRIBUTE_NAME_MAP[attributeName]} Results for ASIN ${item.asin} in Marketplace ${item.marketplace}`,
                asin: item,
                service: VALIDATION_ATTRIBUTE_NAME_MAP[attributeName],
                waitingPassException: false,
                receivedPassException: false,
                correctCatalogDefaultsExpandedVisible: true,
                passExceptionExpandedVisible: true,
                waitingPublishCorrectCatalogDefaults: false,
                receivedPublishCorrectCatalogDefaults: false,
                waitingGetCorrectCatalogDefaults: true,
                receivedGetCorrectCatalogDefaults: false,
                catalogDefaultsValues: undefined,
                catalogDefaultsValuesLookup: {},
                submittedPublishCorrectCatalogDefaults: [],
              })
            )
          }
        >
          {VALIDATION_ATTRIBUTE_OVERALL_RESULT[overallStatus]}
        </a>
      )}
    </>
  );

  if (audit === undefined) {
    return statusIcon;
  }

  const entity = audit.lastUpdatedBy!!.identity;
  return (
    <>
      {statusIcon}
      <Popover
        dismissButton={false}
        position="top"
        size="medium"
        triggerType="text"
        content={
          <React.Fragment>
            Validation requested by {entity}
            <br />
            Result reported at{" "}
            {serviceToDisplay(audit.lastUpdatedAt!!, "yyyy/MM/DD HH:mmA")} PT
          </React.Fragment>
        }
      >
        {" "}
        ...
      </Popover>
    </>
  );
}

export function displayValidationAttributeCell(
  attributeName: keyof FullAsin,
  modalAttributeName: keyof FullAsin,
  item: FullAsinWithAudits
) {
  const modalData = item[
    modalAttributeName as keyof FullAsinWithAudits
  ] as string;
  const overallResult = item[
    attributeName as keyof FullAsinWithAudits
  ] as ValidationAttributeOverallStatus;

  if (!overallResult || !modalData) {
    return (
      <div>
        <Icon name="status-stopped" variant="subtle" />
        &nbsp;<a>No Data</a>
      </div>
    );
  }

  // DLCS returns this as an escaped string.
  const escapedModalData = unescape(modalData);
  let validationAttribute;
  try {
    validationAttribute = JSON.parse(escapedModalData) as ValidationAttribute;
  } catch (e: any) {
    return (
      <div>
        <Icon name="status-stopped" variant="subtle" />
        &nbsp;<a>Data Parse Error</a>
      </div>
    );
  }

  if (validationAttribute === undefined) {
    return "Result Parse Error";
  }

  const audit = item[`${attributeName}Audits` as keyof FullAsinWithAudits];
  return validationAttributeCellWithAudit(
    item,
    attributeName,
    overallResult,
    validationAttribute,
    audit as AsinAudit
  );
}

export function displayValidationSummaryCell(
  attributeName: keyof FullAsin,
  item: FullAsinWithAudits
) {
  const val = item[attributeName as keyof FullAsinWithAudits];
  if (!val || typeof val !== "string") {
    return "-";
  }
  const validations = val.split(",").map((str) => str.trim());
  const denominator = validations.length;
  const numerator = validations.filter((str) => str.includes("PASS")).length;
  const icon =
    denominator === numerator ? (
      <Icon name="status-positive" variant="success" />
    ) : (
      <Icon name="status-negative" variant="error" />
    );
  return (
    <div>
      {icon}
      &nbsp;
      <>
        {numerator} / {denominator} passed
      </>
    </div>
  );
}

export function displayAttributeCell(
  attributeName: keyof FullAsin,
  item: FullAsinWithAudits,
  formatter: (val: any) => string = (x) => x
) {
  const val = item[attributeName as keyof FullAsinWithAudits];
  if (!val) {
    return "-";
  }
  const audit = item[`${attributeName}Audits` as keyof FullAsinWithAudits];
  if (audit === undefined) {
    return formatter(val);
  }
  return cellWithAudit(formatter(val), audit as AsinAudit);
}

export function displayMapAttributeWithMainKeyCell(
  attributeName: keyof FullAsin,
  item: FullAsinWithAudits,
  formatter: (val: any, mainKey: string) => string = (x) => x,
  mainKey: string = ""
) {
  const val = item[attributeName as keyof FullAsinWithAudits];
  if (!val) {
    return "-";
  }
  const mainValue = item[mainKey as keyof FullAsinWithAudits];

  const audit = item[`${attributeName}Audits` as keyof FullAsinWithAudits];
  if (audit === undefined) {
    return formatter(val, mainValue as string);
  }
  return cellWithAudit(formatter(val, mainValue as string), audit as AsinAudit);
}

export const COLUMN_DEFINITIONS: any = [
  {
    id: "announceDate",
    header: "Announce Date",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("announceDate", item, serviceToDisplay),
    sortingField: "announceDate",
  },
  {
    id: "onlineStreetDate",
    header: "Online Street Date",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("onlineStreetDate", item, serviceToDisplay),
    sortingField: "onlineStreetDate",
  },
  {
    id: "programName",
    header: "Program",
    cell: (item: FullAsinWithAudits | RestrictedAsin) =>
      item.isRestricted ? (
        <span
          style={{ fontStyle: "italic" }}
          children={formattedRestrictedPorAsinProgramName(item)}
        />
      ) : (
        displayAttributeCell("programName", item)
      ),
    sortingField: "programName",
  },
  {
    id: "marketplace",
    header: "Marketplace",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("marketplace", item),
  },
  {
    id: "asin",
    header: "ASIN",
    cell: (item: FullAsinWithAudits) => (
      <AsinDisplayCell item={item} componentName={"asinCentralTable"} />
    ),
  },
  {
    id: "asinParty",
    header: "ASIN Partiness",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("asinParty", item),
    sortingField: "asinParty",
  },
  {
    id: "asinType",
    header: "ASIN Type",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("asinType", item, (v) => LAUNCH_ASIN_TYPES[v] || v),
    sortingField: "asinType",
  },
  {
    id: "baselineStatus",
    header: "Baseline Status",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("baselineStatus", item),
    sortingField: "baselineStatus",
  },
  {
    id: "userDescription",
    header: "User Description",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("userDescription", item),
    sortingField: "userDescription",
  },
  {
    id: "description",
    header: "Description",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("description", item, (v) =>
        ["soft_bundle", "vmp_bundle"].includes(item.asinType)
          ? formattedBundleDescription(v)
          : v
      ),
    sortingField: "description",
  },
  {
    id: "partNumber",
    header: "Part Number",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("partNumber", item),
    sortingField: "partNumber",
  },
  {
    id: "productLineName",
    header: "Product Line Name",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("productLineName", item),
    sortingField: "productLineName",
  },
  {
    id: "bundleBaseComponent",
    header: "Bundle Base Component",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("bundleBaseComponent", item),
    sortingField: "bundleBaseComponent",
  },
  {
    id: "catalogLiveness",
    header: "Catalog Liveness",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("catalogLiveness", item),
    sortingField: "catalogLiveness",
  },
  {
    id: "liveness",
    header: "Liveness",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("liveness", item),
    sortingField: "liveness",
  },
  {
    id: "bundleComponents",
    header: "Bundle Components",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("bundleComponents", item),
    sortingField: "bundleComponents",
  },
  {
    id: "bundleComponentQuantity",
    header: "Bundle Component Quantity",
    cell: (item: FullAsinWithAudits) =>
      displayMapAttributeWithMainKeyCell(
        "bundleComponentQuantity",
        item,
        convertMapWithMainKeyToString,
        "bundleBaseComponent"
      ),
    sortingField: "bundleComponents",
  },
  {
    id: "productLineId",
    header: "Product Line Id",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("productLineId", item),
    sortingField: "productLineId",
  },
  {
    id: "itemType",
    header: "Item Type",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("itemType", item),
    sortingField: "itemType",
  },
  {
    id: "salesChannel",
    header: "Sales Channel",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("salesChannel", item),
    sortingField: "salesChannel",
  },
  {
    id: "programId",
    header: "Program Id",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("programId", item),
    sortingField: "programId",
  },
  {
    id: "upc",
    header: "UPC",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("upc", item),
    sortingField: "upc",
  },
  {
    id: "dtcp",
    header: "DTCP",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("dtcp", item),
    sortingField: "dtcp",
  },
  {
    id: "productionFamily",
    header: "Production Family",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("productionFamily", item),
    sortingField: "productionFamily",
  },
  {
    id: "gtin",
    header: "GTIN",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("gtin", item),
    sortingField: "gtin",
  },
  {
    id: "allowListedDeviceCode",
    header: "Allow Listed Device Code",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("allowListedDeviceCode", item),
    sortingField: "allowListedDeviceCode",
  },
  {
    id: "allowListedDtid",
    header: "Allow Listed DTID",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("allowListedDtid", item),
    sortingField: "allowListedDtid",
  },
  {
    id: "bomDtids",
    header: "BOM DTID(s)",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("bomDtids", item),
    sortingField: "bomDtids",
  },
  {
    id: "domainName",
    header: "Domain Name",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("domainName", item),
    sortingField: "domainName",
  },
  {
    id: "makoWeblabStatus",
    header: "Weblab Status",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("makoWeblabStatus", item),
    sortingField: "makoWeblabStatus",
  },
  {
    id: "buyBoxStatus",
    header: "BuyBox Status",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("buyBoxStatus", item),
    sortingField: "buyBoxStatus",
  },
  {
    id: "softwareOutOfBoxExperience",
    header: "Software Out Of Box Experience",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("softwareOutOfBoxExperience", item),
    sortingField: "softwareOutOfBoxExperience",
  },
  {
    id: "validationStatus",
    header: "Validation Status Summary",
    cell: (item: FullAsinWithAudits) =>
      displayValidationSummaryCell("validationStatus", item),
  },
  {
    id: "validationOverallResultHattrick",
    header: "Hattrick Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultHattrick",
        "validationDetailResultHattrick",
        item
      ),
    sortingField: "validationOverallResultHattrick",
  },
  {
    id: "validationOverallResultLVS",
    header: "LVS Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultLVS",
        "validationDetailResultLVS",
        item
      ),
    sortingField: "validationOverallResultLVS",
  },
  {
    id: "validationOverallResultManualDGL",
    header: "DGL Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualDGL",
        "validationDetailResultManualDGL",
        item
      ),
    sortingField: "validationOverallResultManualDGL",
  },
  {
    id: "validationOverallResultManualRL",
    header: "RL Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualRL",
        "validationDetailResultManualRL",
        item
      ),
    sortingField: "validationOverallResultManualRL",
  },
  {
    id: "validationOverallResultManualInstock",
    header: "Instock Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualInstock",
        "validationDetailResultManualInstock",
        item
      ),
    sortingField: "validationOverallResultManualInstock",
  },
  {
    id: "validationOverallResultManualOX",
    header: "OX Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualOX",
        "validationDetailResultManualOX",
        item
      ),
    sortingField: "validationOverallResultManualOX",
  },
  {
    id: "validationOverallResultManualGTM",
    header: "GTM Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualGTM",
        "validationDetailResultManualGTM",
        item
      ),
    sortingField: "validationOverallResultManualGTM",
  },
  {
    id: "validationOverallResultManualCS",
    header: "CS Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualCS",
        "validationDetailResultManualCS",
        item
      ),
    sortingField: "validationOverallResultManualCS",
  },
];

export const porKey = (entry: FullAsinWithAudits | RestrictedAsin) => {
  if (!entry.isRestricted) {
    return `${entry.asin},${entry.marketplace}`;
  }
  return entry.restrictedProgramId;
};

export function SET_ATTRIBUTES_SUCCESS_ITEMS(editedRowSet: {
  [p: number]: boolean;
}): FlashbarProps.MessageDefinition {
  return {
    type: "success",
    header: "Success",
    dismissible: true,
    content: <div>{Object.keys(editedRowSet).length} ASINs Updated.</div>,
  };
}

export function SET_ASIN_MARKETPLACE_DATES_SUCCESS_ITEMS(
  asinMarketplaceDates: AsinMarketplaceDate[]
): FlashbarProps.MessageDefinition {
  return {
    type: "success",
    header: "Success",
    dismissible: true,
    content: <div>{asinMarketplaceDates.length} ASINs Updated Dates.</div>,
  };
}

export function SET_ASIN_MARKETPLACE_ATTRIBUTES_SUCCESS_ITEMS(
  asinMarketplaceAttributes: FullAsinWithAudits[]
): FlashbarProps.MessageDefinition {
  return {
    type: "success",
    header: "Success",
    dismissible: true,
    content: (
      <div>
        {asinMarketplaceAttributes.length} ASINs Updated Attributes. Please wait
        a few minutes for data to propagate
      </div>
    ),
  };
}

export function SET_ATTRIBUTES_PARTIAL_SUCCESS_ITEMS(
  updatedEntries: FullAsinWithAudits[]
): any {
  return {
    type: "error",
    header: "Failed to update some POR ASINs",
    dismissible: true,
    content: (
      <div>
        {updatedEntries.length} POR ASINs were updated, please verify the
        changes and try again.
      </div>
    ),
  };
}

export function RESTRICTED_SEARCH_MESSAGE_ITEMS(
  restrictedPrograms: string[]
): FlashbarProps.MessageDefinition {
  return {
    type: "info",
    header: "Restricted ASINs",
    dismissible: false,
    content: (
      <div>
        Some ASINs have been restricted in the results because access to their
        programs is restricted, download the list of restricted programs on this
        page{" "}
        <Link
          children={"here"}
          color={"inverted"}
          onFollow={() =>
            exportCSV({
              fileName: getTimestampedCsvFileName("RestrictedPrograms"),
              csvData: [["Restricted Programs"]].concat(
                restrictedPrograms.map((it) => [it])
              ) as [[]],
            })
          }
        />
        .<br />
        To request access to the program, please{" "}
        <Link
          external
          externalIconAriaLabel="Opens in a new tab"
          href={PPLS_REQUEST_ACCESS}
          color={"inverted"}
        >
          file a SIM with the device economics team
        </Link>
        .
      </div>
    ),
  };
}
